import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog, DialogTitle, DialogContent, Box, Typography, Tooltip, IconButton, Button } from '@mui/material';

import { Vehicle } from '../../../types/vehicle';
import { createBlankVehicle, cloneLastVehicle } from './fleet-operations/utils';
import VehicleInfo from './fleet-operations/VehicleInfo';
import { toggleVehicleBlock } from './fleet-operations/toggleVehicleBlock';
import FleetTimelineVisualization from './auto-fleet-generator/FleetTimelineVisualization';
import { GeneratedShift } from './auto-fleet-generator/types';
import { logEvent } from '../../../services/analytics';

import { Icon } from '@liftango/ui-icons';
interface FleetOperationsModalProps {
  open: boolean;
  onClose: () => void;
  fleetName: string;
  vehicles: Vehicle[];
  onEditVehicle: (vehicle: Vehicle) => void;
  onAddVehicle: (vehicle: Vehicle) => void;
  onUpdateVehicle: (vehicle: Vehicle) => void;
  onDeleteVehicle: (vehicleId: string) => void;
  /** Still accepted for API back-compat (used elsewhere via FleetMenu); not consumed here. */
  points: { id: string; name: string }[];
}

const TIMELINE_INTERVAL_MINUTES = 30;
const DEFAULT_SERVICE_START_MIN = 6 * 60;   // 06:00
const DEFAULT_SERVICE_END_MIN = 22 * 60;    // 22:00

function parseTime(hhmm: string): number {
  const [h, m] = hhmm.split(':').map(Number);
  return h * 60 + m;
}

const FleetOperationsModal: React.FC<FleetOperationsModalProps> = ({
  open,
  onClose,
  fleetName,
  vehicles,
  onEditVehicle,
  onAddVehicle,
  onUpdateVehicle,
  onDeleteVehicle,
}) => {
  const { t } = useTranslation('mapCore');
  const [seatDrafts, setSeatDrafts] = useState<{ [id: string]: { standardSeats: number; wheelchairSeats: number } }>({});

  const { serviceStartMinutes, serviceEndMinutes } = useMemo(() => {
    let min = Infinity;
    let max = -Infinity;
    for (const v of vehicles) {
      for (const p of v.operatingHours) {
        const s = parseTime(p.start);
        const e = parseTime(p.end);
        if (s < min) min = s;
        if (e > max) max = e;
      }
    }
    const start = isFinite(min)
      ? Math.min(DEFAULT_SERVICE_START_MIN, Math.floor(min / TIMELINE_INTERVAL_MINUTES) * TIMELINE_INTERVAL_MINUTES)
      : DEFAULT_SERVICE_START_MIN;
    const end = isFinite(max)
      ? Math.max(DEFAULT_SERVICE_END_MIN, Math.ceil(max / TIMELINE_INTERVAL_MINUTES) * TIMELINE_INTERVAL_MINUTES)
      : DEFAULT_SERVICE_END_MIN;
    return { serviceStartMinutes: start, serviceEndMinutes: end };
  }, [vehicles]);

  const shifts: GeneratedShift[] = useMemo(
    () => vehicles.map(v => ({
      vehicleId: v.id,
      vehicleName: v.name,
      operatingPeriods: v.operatingHours,
    })),
    [vehicles],
  );

  const handleBlockClick = (
    vehicleId: string,
    blockStartMinutes: number,
    fromStatus: 'working' | 'break' | 'off',
  ) => {
    const vehicle = vehicles.find(v => v.id === vehicleId);
    if (!vehicle) return;
    const updated = toggleVehicleBlock(vehicle, blockStartMinutes, TIMELINE_INTERVAL_MINUTES);
    onUpdateVehicle(updated);
    logEvent({
      category: 'Fleet Management',
      action: 'FleetTimelineBlockToggled',
      label: vehicle.name,
      vehicle_id: vehicleId,
      fleet_name: fleetName,
      block_start_minutes: blockStartMinutes,
      from_status: fromStatus,
    });
  };

  const handleSeatChange = (vehicle: Vehicle, field: 'standardSeats' | 'wheelchairSeats', value: number) => {
    setSeatDrafts(prev => ({
      ...prev,
      [vehicle.id]: {
        standardSeats: prev[vehicle.id]?.standardSeats ?? vehicle.standardSeats,
        wheelchairSeats: prev[vehicle.id]?.wheelchairSeats ?? vehicle.wheelchairSeats,
        [field]: value,
      },
    }));
  };

  const handleSeatBlurOrEnter = (vehicle: Vehicle) => {
    const draft = seatDrafts[vehicle.id];
    if (draft && (draft.standardSeats !== vehicle.standardSeats || draft.wheelchairSeats !== vehicle.wheelchairSeats)) {
      logEvent({
        category: 'Fleet Management',
        action: 'VehicleCapacityUpdated',
        label: vehicle.name,
        vehicle_id: vehicle.id,
        fleet_name: fleetName,
        old_standard_seats: vehicle.standardSeats,
        new_standard_seats: draft.standardSeats,
        old_wheelchair_seats: vehicle.wheelchairSeats,
        new_wheelchair_seats: draft.wheelchairSeats,
      });
      onUpdateVehicle({ ...vehicle, ...draft });
    }
  };

  const handleAddSimilarVehicle = () => {
    const newVehicle = cloneLastVehicle(vehicles);
    logEvent({
      category: 'Fleet Management',
      action: 'VehicleAddedFromTimelineFooter',
      label: newVehicle.name,
      vehicle_id: newVehicle.id,
      fleet_name: fleetName,
      cloned_from_last: vehicles.length > 0,
    });
    onAddVehicle(newVehicle);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth sx={{ zIndex: 2500 }}>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <span>{t('fleetOpsModal.title', { fleetName })}</span>
        <Button
          variant="contained"
          startIcon={<Icon size={20} name="plus" />}
          size="small"
          onClick={() => onAddVehicle(createBlankVehicle(vehicles))}
        >
          {t('fleetOpsModal.addVehicle')}
        </Button>
      </DialogTitle>
      <DialogContent>
        {vehicles.length > 0 ? (
          <Box sx={{ mb: 2 }}>
            <FleetTimelineVisualization
              vehicles={shifts}
              serviceStartMinutes={serviceStartMinutes}
              serviceEndMinutes={serviceEndMinutes}
              intervalMinutes={TIMELINE_INTERVAL_MINUTES}
              peakPeriods={[]}
              interactive={true}
              onBlockClick={handleBlockClick}
              vehicleColumnWidthPx={260}
              renderVehicleCell={(shift) => {
                const vehicle = vehicles.find(v => v.id === shift.vehicleId);
                if (!vehicle) return null;
                const draft = seatDrafts[vehicle.id] || { standardSeats: vehicle.standardSeats, wheelchairSeats: vehicle.wheelchairSeats };
                return (
                  <VehicleInfo
                    vehicle={vehicle}
                    draft={draft}
                    onEditVehicle={onEditVehicle}
                    onSeatChange={handleSeatChange}
                    onSeatBlurOrEnter={handleSeatBlurOrEnter}
                  />
                );
              }}
              renderVehicleRowSuffix={(shift) => (
                <Tooltip
                  title={t('fleetOpsModal.deleteVehicle')}
                  PopperProps={{ sx: { zIndex: 2600 } }}
                >
                  <IconButton
                    size="small"
                    onClick={() => onDeleteVehicle(shift.vehicleId)}
                    sx={{ color: 'text.secondary' }}
                  >
                    <Icon name="trash" size={20} />
                  </IconButton>
                </Tooltip>
              )}
            />
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              {t('fleetOpsModal.timelineHint')}
            </Typography>
            <Box sx={{ mt: 1.5, display: 'flex', justifyContent: 'flex-start' }}>
              <Tooltip
                title={t('fleetOpsModal.addSimilarVehicleTooltip', {
                  defaultValue: 'Add another vehicle with the same attributes as the last one',
                })}
                PopperProps={{ sx: { zIndex: 2600 } }}
              >
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<Icon size={20} name="plus" />}
                  onClick={handleAddSimilarVehicle}
                >
                  {t('fleetOpsModal.addSimilarVehicle', { defaultValue: 'Add another' })}
                </Button>
              </Tooltip>
            </Box>
          </Box>
        ) : (
          <Box sx={{ py: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <Typography variant="body2" color="text.secondary">
              {t('fleetOpsModal.empty', { defaultValue: 'No vehicles yet. Click "Add Vehicle" to start.' })}
            </Typography>
            <Box>
              <Button
                variant="outlined"
                size="small"
                startIcon={<Icon size={20} name="plus" />}
                onClick={() => onAddVehicle(createBlankVehicle(vehicles))}
              >
                {t('fleetOpsModal.addVehicle')}
              </Button>
            </Box>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default FleetOperationsModal;
